import { FC, useEffect, useState } from 'react'
import OverviewTable from './components/OverviewTable'
import { IProductDimension } from 'shared/models/IProductDimension'
import { Loader } from 'shared/components/Loader'
import { connect } from 'react-redux'
import { IApplicationState } from 'redux/store/store'
import { ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import {
  clearErrorActionCreator,
  deleteProductDimensionActionCreator,
  getProductDimensionsActionCreator,
  IDeleteProductDimensionResponseAction,
  IGetProductDimensionsResponseAction,
  IPostProductDimensionResponseAction,
  IUpdateProductDimensionResponseAction,
  postProductDimensionActionCreator,
  updateProductDimensionActionCreator,
} from 'redux/store/actions/productDimensionAction'
import DetailsModal from './components/DetailsModal/DetailsModal'
import MessageModal from 'shared/components/MessageModal'
import { Answer, MessageType } from 'shared/models/Enum'

interface IProps {
  isLoading: boolean
  errorMessage: string
  productDimensions: IProductDimension[]
  warehouseId: number
  getProductDimensions: () => Promise<IGetProductDimensionsResponseAction>
  postProductDimension: (
    productDimension: IProductDimension
  ) => Promise<IPostProductDimensionResponseAction>
  updateProductDimension: (
    productDimension: IProductDimension
  ) => Promise<IUpdateProductDimensionResponseAction>
  deleteProductDimension: (
    productDimensionId: number
  ) => Promise<IDeleteProductDimensionResponseAction>
  clearError: () => void
}

const ProductDimensionList: FC<IProps> = ({
  isLoading,
  errorMessage,
  productDimensions,
  warehouseId,
  getProductDimensions,
  postProductDimension,
  updateProductDimension,
  deleteProductDimension,
  clearError,
}) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [selectedProductDimension, setSelectedProductDimension] =
    useState<IProductDimension | null>(null)

  useEffect(() => {
    getProductDimensions()
  }, [getProductDimensions])

  const handleNew = () => {
    setSelectedProductDimension(null)
    setShowDetailsModal(true)
  }

  const handleShowDetails = (productDimension: IProductDimension) => {
    setSelectedProductDimension(productDimension)
    setShowDetailsModal(true)
  }

  const handleHideDetails = () => {
    setSelectedProductDimension(null)
    setShowDetailsModal(false)
    clearError()
  }

  const handleSubmitDetails = (productDimension: IProductDimension) => {
    const updatedProductDimension = {
      ...selectedProductDimension,
      ...productDimension,
    }
    if (updatedProductDimension.productDimensionId) {
      updateProductDimension(updatedProductDimension).then(response => {
        if (response.success) {
          setShowDetailsModal(false)
          getProductDimensions()
        }
      })
    } else {
      postProductDimension(productDimension).then(response => {
        if (response.success) {
          setShowDetailsModal(false)
          getProductDimensions()
        }
      })
    }
  }

  const handleDelete = (answer: Answer) => {
    if (answer === Answer.Yes && selectedProductDimension?.productDimensionId) {
      deleteProductDimension(selectedProductDimension.productDimensionId).then(
        response => {
          if (response.success) {
            setShowDeleteConfirmation(false)
            getProductDimensions()
          }
        }
      )
    } else {
      setShowDeleteConfirmation(false)
      setShowDetailsModal(true)
    }
  }

  const onDelete = () => {
    setShowDeleteConfirmation(true)
    setShowDetailsModal(false)
  }

  // Only show full-page loader when NOT in a modal (to prevent unmounting the modal during API calls)
  if (isLoading && !showDetailsModal && !showDeleteConfirmation) {
    return <Loader show={isLoading} />
  }

  return (
    <main>
      <h2>Productafmetingen Onderhouden</h2>
      {errorMessage && !showDetailsModal && (
        <div className='alert alert-danger'>{errorMessage}</div>
      )}
      <section>
        <OverviewTable
          productDimensions={productDimensions}
          warehouseId={warehouseId}
          showDetails={handleShowDetails}
          handleNew={handleNew}
        />
      </section>

      {showDetailsModal && (
        <DetailsModal
          show={showDetailsModal}
          productDimension={selectedProductDimension}
          isLoading={isLoading}
          errorMessage={errorMessage}
          onSubmit={handleSubmitDetails}
          onHide={handleHideDetails}
          onDelete={onDelete}
        />
      )}
      {showDeleteConfirmation && (
        <MessageModal
          showModal={showDeleteConfirmation}
          title='Productafmeting verwijderen'
          message='Weet u zeker dat u deze productafmeting wilt verwijderen?'
          messageType={MessageType.YesNo}
          onAnswer={handleDelete}
        />
      )}
    </main>
  )
}

const mapStateToProps = ({
  productDimensionState: { loading, foutmelding, productDimensions },
  globalState: { vestiging },
}: IApplicationState) => {
  return {
    isLoading: loading,
    errorMessage: foutmelding,
    productDimensions,
    warehouseId: vestiging,
  }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    getProductDimensions: () => dispatch(getProductDimensionsActionCreator()),
    postProductDimension: (productDimension: IProductDimension) =>
      dispatch(postProductDimensionActionCreator(productDimension)),
    updateProductDimension: (productDimension: IProductDimension) =>
      dispatch(updateProductDimensionActionCreator(productDimension)),
    deleteProductDimension: (productDimensionId: number) =>
      dispatch(deleteProductDimensionActionCreator(productDimensionId)),
    clearError: () => dispatch(clearErrorActionCreator()),
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProductDimensionList)
